import {
    EditOutlined,
    QuestionCircleOutlined,
    DeleteOutlined,
    PlusOutlined,
    CheckOutlined,
    CloseOutlined,
    AppstoreAddOutlined,
    MenuOutlined,
    EyeInvisibleOutlined,
} from "@ant-design/icons";
import CellInputPopup from "./CellInputPopup";
import {
    Button,
    Col,
    Divider,
    Form,
    Input,
    Popconfirm,
    Popover,
    Row,
    Select,
    Space,
    Switch,
    Table,
    message,
    Collapse,
} from "antd";
import _, { cloneDeep } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import TableBorderEditor from "./TableBorder";
import TablePaddingEditor from "./TablePadding";
import { CopyIcon } from "src/components";
import FeedbackAndEvaluation from "./FeedbackAndEvaluation";
import InteractionLimitEditor from "./InteractionLimitEditor";
import Tappable from "./Tappable";
import AlignmentComponent from "./Alignment";
import MinMaxWidthComponent from "./MinMaxWidth";
import MinHeight from "./MinHeight";
import CellInnerContentPreview from "./CellInnerContentPreview";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import TableToolbar from "../../AllInOne/renderEditor/ComponentsEditor/GenericTable/Toolbar/TableToolbar";
import { v4 as uuid } from "uuid";

const { Panel } = Collapse;

const DEFAULT_CELL = {
    tappable: {
        enabled: false,
        default: {
            selected: "0",
        },
    },
    text: {
        enabled: false,
        fontSize: {
            default: 16,
        },
        alignment: {
            default: "CENTER",
        },
        value: {
            default: "",
        },
        color: {
            default: "#000000",
        },
    },
    input: {
        enabled: false,
        default: "",
        fill: "#ffffff",
        min: "0",
        max: "",
        min_feedback_text: "",
        max_feedback_text: "",
        min_feedback_audio: "",
        max_feedback_audio: "",
    },
    dropdown: {
        enabled: false,
        default: "",
        optionVariable: "",
    },
    image: {
        enabled: false,
        src: {
            default: "",
        },
    },
    fillColor: {
        enabled: false,
        default: "#F5F5F5",
    },
    clickable: {
        enabled: false,
        // linked_compute_function: "",
    },
};

const modifyCellForDuplicate = (
    cell: any,
    rowIndex: number,
    columnIndex: number,
    hadTags: boolean = false,
) => {
    let newCell = _.cloneDeep(cell);
    newCell.id = `${rowIndex}-${columnIndex}-${Math.random()
        .toString(36)
        .substring(7)}`;
    if (newCell.linked_global_context_variable) {
        newCell.linked_global_context_variable.name = null;
        newCell.value = null;
    }
    if (!hadTags && cell.dragDrop?.enabled && cell.dragDrop?.droppedSource) {
        newCell.dragDrop.droppedSource = null;
    }
    return newCell;
};

const TableEditor = ({
    disableWrite,
    table,
    tableVariableName,
    setBlock,
    dropdownGlobalVariables,
    currentEditor,
    setCurrentEditor,
    hasMentions,
    mentionsList = [],
    renderLinkSelect,
    tagsList,
    optionsSchema,
    tagsClusterList,
}: any) => {
    const { cells = [] } = table;

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [idsGenerated, setIdsGenerated] = useState(false);
    const [curCellRow, setCurCellRow] = useState(-1);
    const [curCellCol, setCurCellCol] = useState(-1);
    const [inputRow, setInputRow] = useState(1);
    const [inputCol, setInputCol] = useState(1);
    const [curCell, setCurCell] = useState({});
    const [tempConfig, setTempConfig] = useState("");
    const [selectedCells, setSelectedCells] = useState<[number, number][]>([]);

    useEffect(() => {
        if (!idsGenerated) {
            let tmpBlock = _.cloneDeep(table);
            let tempData = tmpBlock?.cells;
            let idsExist = true;
            tempData.forEach((row: any, i: number) => {
                row.forEach((cell: any, j: number) => {
                    // Generate a randomized string prefix for the cell id
                    // to avoid id conflicts
                    const idPrefix = Math.random().toString(36).substring(7);
                    if (cell.id === undefined) {
                        cell.id = i + "-" + j + "-" + idPrefix;
                        idsExist = false;
                    }
                });
            });
            setIdsGenerated(true);
            if (!idsExist) {
                tmpBlock.cells = tempData;
                setBlock(tmpBlock);
            }
        }
    }, [table, idsGenerated]);

    const showModal = (row: number, column: number, cell: any) => {
        setCurCellRow(row);
        setCurCellCol(column);
        setCurCell(cell);
        setIsModalOpen(true);
    };

    const closeForm = () => {
        setCurCellRow(-1);
        setCurCellCol(-1);
        setCurCell({});
        setIsModalOpen(false);
    };

    const handleDuplicateRow = (row: number) => {
        let tmpBlock = _.cloneDeep(table);
        let tempData = tmpBlock?.cells;
        tempData.splice(
            row + 1,
            0,
            cells[row].map((cell: any, columnIndex: number) => {
                return modifyCellForDuplicate(cell, row + 1, columnIndex);
            }),
        );
        tmpBlock.cells = tempData;
        setBlock(tmpBlock);
    };

    const handleDuplicateColumn = (column: number) => {
        let tmpBlock = _.cloneDeep(table);
        let tempData = tmpBlock?.cells;
        tempData.forEach((row: any, i: number) => {
            row.splice(
                column + 1,
                0,
                modifyCellForDuplicate(row[column], i, column + 1),
            );
        });
        tmpBlock.cells = tempData;
        setBlock(tmpBlock);
    };

    const allIndicesPresent = useMemo(() => {
        if (!cells?.length || !cells[0]?.length) return false;
        const cellIndices = cells.flatMap((row, i) =>
            row.map((_, j) => [i, j]),
        );
        return cellIndices.every(([i, j]) =>
            selectedCells.some(([x, y]) => x === i && y === j),
        );
    }, [selectedCells, cells]);

    const [selectedRows, selectedCols] = useMemo(() => {
        if (!selectedCells?.length) return [[], []];
        const maxRow = cells.length - 1;
        const maxCol = cells[0].length - 1;

        // Check if all elements of each row are present in selectedCells
        const rows = [];
        for (let i = 0; i <= maxRow; i++) {
            const row = cells[i];
            const rowCells = row.every((_, j) =>
                selectedCells.some(([x, y]) => x === i && y === j),
            );
            if (rowCells) {
                rows.push(i);
            }
        }

        // Check if all elements of each column are present in selectedCells
        const cols = [];
        for (let j = 0; j <= maxCol; j++) {
            const colCells = cells
                .map((row, i) =>
                    selectedCells.some(([x, y]) => x === i && y === j),
                )
                .every(Boolean);
            if (colCells) {
                cols.push(j);
            }
        }

        return [rows, cols];
    }, [selectedCells]);

    useEffect(() => {
        const handlePaste = (event) => {
            if (disableWrite) return;
            const activeElement = document?.activeElement;
            const tagName = activeElement?.tagName
                ? activeElement.tagName.toLowerCase()
                : "";
            const isEditable =
                (activeElement instanceof HTMLElement &&
                    activeElement.isContentEditable) ||
                tagName === "textarea" ||
                tagName === "input";

            if (!isEditable) {
                if (event.ctrlKey && event.key === "v") {
                    event.preventDefault(); // Prevent default paste behavior
                    handlePasteToCells();
                }
            }
        };

        document.body.addEventListener("keydown", handlePaste);

        return () => {
            document.body.removeEventListener("keydown", handlePaste);
        };
    }, [selectedCells, cells]);

    const columns = [
        {
            dataIndex: "drag",
            key: "drag",
            title: (
                <Button
                    disabled={disableWrite}
                    shape="circle"
                    type={allIndicesPresent ? "primary" : "default"}
                    size="small"
                    icon={<CheckOutlined />}
                    onClick={() => {
                        if (allIndicesPresent) setSelectedCells([]);
                        else {
                            setSelectedCells(
                                cells.flatMap((row, i) =>
                                    row.map((_, j) => [i, j]),
                                ),
                            );
                        }
                    }}
                />
            ),
            render: (val: number, __: any, index: number) => (
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px",
                    }}
                >
                    <Button
                        disabled={disableWrite}
                        shape="circle"
                        type={
                            selectedRows.includes(index) ? "primary" : "default"
                        }
                        size="small"
                        icon={<CheckOutlined />}
                        onClick={() => {
                            if (selectedRows.includes(index))
                                setSelectedCells(
                                    selectedCells?.filter(
                                        (v) => v[0] !== index,
                                    ),
                                );
                            else {
                                setSelectedCells([
                                    ...selectedCells?.filter(
                                        (v) => v[0] !== index,
                                    ),
                                    ...cells[0]?.map((__, i) => [index, i]),
                                ]);
                            }
                        }}
                    />
                    <MenuOutlined />
                </div>
            ),
        },
        ...(cells[0] || [])?.map((item: any, i: number) => {
            return {
                width: "auto",
                dataIndex: i,
                key: i,
                title: (
                    <>
                        <Space>
                            <Popconfirm
                                title={"Delete this column?"}
                                icon={
                                    <QuestionCircleOutlined
                                        style={{ color: "red" }}
                                    />
                                }
                                disabled={disableWrite}
                                onConfirm={() => {
                                    let tmpBlock = _.cloneDeep(table);
                                    let tempData = tmpBlock?.cells?.map(
                                        (val: any) =>
                                            val.filter(
                                                (_: any, idx: number) =>
                                                    idx !== i,
                                            ),
                                    );
                                    tmpBlock.cells = tempData;
                                    setBlock(tmpBlock);
                                }}
                                okText={"Yes"}
                                cancelText={"Cancel"}
                            >
                                <Button
                                    disabled={disableWrite}
                                    shape="circle"
                                    danger
                                    size="small"
                                    icon={
                                        <DeleteOutlined
                                            style={{ color: "red" }}
                                        />
                                    }
                                />
                            </Popconfirm>
                            <Popconfirm
                                title={"Add a new column?"}
                                disabled={disableWrite}
                                onConfirm={() => {
                                    let tmpBlock = _.cloneDeep(table);

                                    let tempData = tmpBlock?.cells?.map(
                                        (val: any) => {
                                            val.splice(
                                                i + 1,
                                                0,
                                                cloneDeep(DEFAULT_CELL),
                                            );

                                            return val;
                                        },
                                    );
                                    tmpBlock.cells = tempData;
                                    setBlock(tmpBlock);
                                }}
                                okText={"Yes"}
                                cancelText={"Cancel"}
                            >
                                <Button
                                    disabled={disableWrite}
                                    shape="circle"
                                    type="primary"
                                    size="small"
                                    icon={<PlusOutlined />}
                                />
                            </Popconfirm>
                            <Popover
                                placement="left"
                                content="Duplicate Column"
                            >
                                <Popconfirm
                                    title={"Duplicate Column?"}
                                    onConfirm={() => {
                                        handleDuplicateColumn(i);
                                    }}
                                    okText={"Yes"}
                                    cancelText={"Cancel"}
                                    disabled={disableWrite}
                                >
                                    <Button
                                        disabled={disableWrite}
                                        size="small"
                                        shape="circle"
                                        type="primary"
                                        icon={<AppstoreAddOutlined />}
                                    />
                                </Popconfirm>
                            </Popover>
                            <Button
                                disabled={disableWrite}
                                shape="circle"
                                type={
                                    selectedCols.includes(i)
                                        ? "primary"
                                        : "default"
                                }
                                size="small"
                                icon={<CheckOutlined />}
                                onClick={() => {
                                    if (selectedCols.includes(i))
                                        setSelectedCells(
                                            selectedCells?.filter(
                                                (v) => v[1] !== i,
                                            ),
                                        );
                                    else {
                                        setSelectedCells([
                                            ...selectedCells?.filter(
                                                (v) => v[1] !== i,
                                            ),
                                            ...cells.map((__, index) => [
                                                index,
                                                i,
                                            ]),
                                        ]);
                                    }
                                }}
                            />
                        </Space>
                        <MinMaxWidthComponent
                            block={
                                table.minMaxWidth ? table.minMaxWidth[i] : {}
                            }
                            disableWrite={disableWrite}
                            setBlock={(val: any) => {
                                let tmpBlock = _.cloneDeep(table);
                                if (!tmpBlock.minMaxWidth)
                                    tmpBlock.minMaxWidth = {};
                                tmpBlock.minMaxWidth[i] = val;
                                setBlock(tmpBlock);
                            }}
                        />
                    </>
                ),
                render: (text: any, __: any, index: number) => {
                    const cellData =
                        index === cells?.length ? null : cells[index][i];

                    const invalidStyle =
                        typeof text === "string" &&
                        text.length < 1 &&
                        cellData?.type === "text"
                            ? { background: "rgba(255,0,0,0.1)" }
                            : {};

                    return {
                        props: {
                            style:
                                index === cells.length
                                    ? {}
                                    : {
                                          borderLeft: "1px solid black",
                                          borderBottom: "1px solid black",
                                          ...invalidStyle,
                                      },
                        },
                        children: (
                            <>
                                {index !== cells.length && (
                                    <div
                                        style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            marginBottom: "10px",
                                        }}
                                    >
                                        <div
                                            style={{
                                                fontSize: "14px",
                                                color: "#555",
                                            }}
                                        >
                                            Cell {index + 1} {i + 1}
                                        </div>
                                        <div>
                                            <Button
                                                disabled={disableWrite}
                                                shape="circle"
                                                type={
                                                    selectedCells?.find(
                                                        (v) =>
                                                            v[0] === index &&
                                                            v[1] === i,
                                                    )
                                                        ? "primary"
                                                        : "default"
                                                }
                                                size="small"
                                                icon={<CheckOutlined />}
                                                onClick={() => {
                                                    if (
                                                        selectedCells?.find(
                                                            (v) =>
                                                                v[0] ===
                                                                    index &&
                                                                v[1] === i,
                                                        )
                                                    )
                                                        setSelectedCells(
                                                            selectedCells?.filter(
                                                                (v) =>
                                                                    !(
                                                                        v[0] ===
                                                                            index &&
                                                                        v[1] ===
                                                                            i
                                                                    ),
                                                            ),
                                                        );
                                                    else {
                                                        setSelectedCells([
                                                            ...selectedCells,
                                                            [index, i],
                                                        ]);
                                                    }
                                                }}
                                            />
                                            <Button
                                                shape="circle"
                                                type={"default"}
                                                size="small"
                                                disabled={disableWrite}
                                                icon={<CopyIcon />}
                                                onClick={() => {
                                                    message.info(
                                                        `Copied Cell data to clipboard!`,
                                                    );
                                                    navigator.clipboard.writeText(
                                                        JSON.stringify({
                                                            ...cellData,
                                                            type: "cell",
                                                        }),
                                                    );
                                                }}
                                            />
                                            <Button
                                                disabled={disableWrite}
                                                shape="circle"
                                                type="default"
                                                icon={<EditOutlined />}
                                                size="small"
                                                onClick={() =>
                                                    showModal(
                                                        index,
                                                        i,
                                                        cellData,
                                                    )
                                                }
                                            />
                                        </div>
                                    </div>
                                )}
                                {/* <div
                                    style={
                                        index === cells.length
                                            ? {}
                                            : {
                                                  minHeight: "30px",
                                                  ...cellStyle,
                                              }
                                    }
                                >
                                    <div
                                        style={{
                                            color: "#666",
                                            textAlign: "center",
                                        }}
                                    >
                                        {cellData?.text?.enabled && "Text "}
                                        {cellData?.image?.enabled && "Image "}
                                        {cellData?.fillColor?.enabled &&
                                            "FillColor "}
                                        {cellData?.input?.enabled && "Input"}
                                        {cellData?.dropdown?.enabled &&
                                            "Dropdown"}
                                        {cellData?.border?.enabled && "Border"}
                                        {cellData?.padding?.enabled &&
                                            "Padding"}
                                        {cellData?.dragDrop?.enabled &&
                                            "DragDrop"}
                                        {" " +
                                            (_.isString(
                                                cellData?.correctInputValues,
                                            )
                                                ? []
                                                : cellData?.correctInputValues
                                            )?.join(", ")}
                                    </div>
                                </div> */}
                                {cellData?.hidden ? (
                                    <div
                                        style={{
                                            background: "#f7f7f7",
                                            width: "100%",
                                            height: "50px",
                                            fontSize: "40px",
                                            color: "#d1d1d1",
                                            display: "flex",
                                            justifyContent: "c enter",
                                        }}
                                    >
                                        <EyeInvisibleOutlined />
                                    </div>
                                ) : (
                                    <>
                                        <CellInnerContentPreview
                                            {...{
                                                tagsList,
                                                optionsSchema,
                                                tagsClusterList,
                                                curCellRow: index,
                                                curCellCol: i,
                                                curCell,
                                                block: table,
                                                setCurrentCell: (
                                                    cell: any,
                                                    data = {},
                                                ) => {
                                                    let tmpBlock =
                                                        cloneDeep(table);
                                                    const tempData =
                                                        tmpBlock?.cells;
                                                    tempData[index][i] = cell;
                                                    tmpBlock.cells = tempData;
                                                    setBlock(tmpBlock, data);
                                                },
                                                disableWrite,
                                                currentEditor,
                                                setCurrentEditor,
                                                hasMentions,
                                                mentionsList,
                                            }}
                                            cell={cellData}
                                            tabIndex={index * 10 + i + 1}
                                        />
                                    </>
                                )}
                                {cellData?.linked_global_context_variable
                                    ?.name && (
                                    <>
                                        <Divider />
                                        <strong
                                            style={{
                                                fontSize: "12px",
                                            }}
                                        >
                                            Linked -{" "}
                                            {
                                                cellData
                                                    ?.linked_global_context_variable
                                                    ?.name
                                            }
                                        </strong>
                                    </>
                                )}
                            </>
                        ),
                    };
                },
            };
        }),
        {
            dataIndex: "index",
            key: "index",
            title: "Row Actions",
            render: (val: number) => {
                return {
                    props: {
                        style:
                            val >= 0
                                ? {
                                      borderLeft: "1px solid black",
                                  }
                                : null,
                    },

                    children:
                        val >= 0 ? (
                            <>
                                {!disableWrite && (
                                    <Space>
                                        {cells?.length > 1 && (
                                            <Popconfirm
                                                title={"Delete this row?"}
                                                icon={
                                                    <QuestionCircleOutlined
                                                        style={{ color: "red" }}
                                                    />
                                                }
                                                onConfirm={() => {
                                                    let tmpBlock =
                                                        _.cloneDeep(table);
                                                    let tempData =
                                                        tmpBlock?.cells;
                                                    tempData.splice(val, 1);
                                                    tmpBlock.cells = tempData;
                                                    setBlock(tmpBlock);
                                                }}
                                                okText={"Yes"}
                                                cancelText={"Cancel"}
                                            >
                                                <Button
                                                    shape="circle"
                                                    danger
                                                    icon={
                                                        <DeleteOutlined
                                                            style={{
                                                                color: "red",
                                                            }}
                                                        />
                                                    }
                                                />
                                            </Popconfirm>
                                        )}
                                        <Popconfirm
                                            title={"Add a row below?"}
                                            onConfirm={() => {
                                                let tmpBlock =
                                                    _.cloneDeep(table);
                                                let tempData = tmpBlock?.cells;
                                                tempData.splice(
                                                    val + 1,
                                                    0,
                                                    cells[0].map(() =>
                                                        cloneDeep(DEFAULT_CELL),
                                                    ),
                                                );
                                                tmpBlock.cells = tempData;
                                                setBlock(tmpBlock);
                                            }}
                                            okText={"Yes"}
                                            cancelText={"Cancel"}
                                        >
                                            <Button
                                                shape="circle"
                                                type="primary"
                                                icon={<PlusOutlined />}
                                            />
                                        </Popconfirm>
                                        <Popover
                                            placement="left"
                                            content="Duplicate Row"
                                        >
                                            <Popconfirm
                                                title={"Duplicate Row?"}
                                                onConfirm={() => {
                                                    handleDuplicateRow(val);
                                                }}
                                                okText={"Yes"}
                                                cancelText={"Cancel"}
                                            >
                                                <Button
                                                    shape="circle"
                                                    type="primary"
                                                    icon={
                                                        <AppstoreAddOutlined />
                                                    }
                                                />
                                            </Popconfirm>
                                        </Popover>
                                    </Space>
                                )}
                                <MinHeight
                                    disableWrite={disableWrite}
                                    block={
                                        table.minHeight
                                            ? table.minHeight[val]
                                            : {}
                                    }
                                    setBlock={(value: any) => {
                                        let tmpBlock = _.cloneDeep(table);
                                        if (!tmpBlock.minHeight)
                                            tmpBlock.minHeight = {};
                                        tmpBlock.minHeight[val] = value;
                                        setBlock(tmpBlock);
                                    }}
                                />
                            </>
                        ) : null,
                };
            },
        },
    ];

    const tableData = [
        ...(cells || [])?.map((item: any, i: number) => {
            const cell: any = { key: i + 1, index: i };

            item?.forEach((_: any, j: number) => {
                cell[j] = "";
            });
            return cell;
        }),
    ];

    const cellInputProps = {
        isModalOpen,
        closeForm,
        curCellRow,
        curCellCol,
        curCell,
        block: table,
        setBlock,
        disableWrite,
        dropdownGlobalVariables,
        categoryLists: table?.dragDrop?.categoryLists,
        tableVariableName,
        currentEditor,
        setCurrentEditor,
        hasMentions,
        mentionsList,
        renderLinkSelect,
        tagsList,
    };

    const handlePasteToCells = async (has_tags = false) => {
        try {
            if (!selectedCells?.length) return;
            const clipboardData = await navigator.clipboard.readText();
            if (!clipboardData) return;
            let data = JSON.parse(clipboardData);
            if (data?.type !== "cell") return;
            delete data.type;
            const tmpBlock = _.cloneDeep(table);
            const newTags: any = [];
            const oldTags: any = [];
            selectedCells.forEach(([row, col]) => {
                let tag = modifyCellForDuplicate(data, row, col, has_tags);
                if (has_tags && tag?.dragDrop?.droppedSource) {
                    if (
                        tmpBlock.cells[row][col]?.dragDrop?.droppedSource
                            ?.genericId
                    ) {
                        tag.dragDrop.droppedSource.genericId =
                            tmpBlock.cells[row][
                                col
                            ].dragDrop.droppedSource.genericId;
                        oldTags.push(tag.dragDrop.droppedSource);
                    } else {
                        tag.dragDrop.droppedSource.genericId = uuid();
                        newTags.push(tag.dragDrop.droppedSource);
                    }
                }
                tmpBlock.cells[row][col] = tag;
            });
            setBlock(tmpBlock, { oldTags, newTags });
            message.success("Cells Updated!");
        } catch (error) {}
    };

    const moveRow = useCallback(
        (dragIndex, hoverIndex) => {
            let tmpBlock = _.cloneDeep(table);
            let dragWidth = tmpBlock?.minHeight?.[dragIndex];
            let dropWidth = tmpBlock?.minHeight?.[hoverIndex];
            tmpBlock.minHeight = {
                ...(tmpBlock?.minHeight || {}),
                [dragIndex]: dropWidth || { enabled: false },
                [hoverIndex]: dragWidth || { enabled: false },
            };
            const dragRow = tmpBlock.cells[dragIndex];
            const newData = [...tmpBlock.cells];
            newData.splice(dragIndex, 1);
            newData.splice(hoverIndex, 0, dragRow);
            tmpBlock.cells = newData;
            setBlock(tmpBlock);
        },
        [table],
    );

    const moveColumn = useCallback(
        (dragIndex, hoverIndex) => {
            let tmpBlock = _.cloneDeep(table);
            let dragWidth = tmpBlock?.minMaxWidth?.[dragIndex - 1];
            let dropWidth = tmpBlock?.minMaxWidth?.[hoverIndex - 1];
            tmpBlock.minMaxWidth = {
                ...(tmpBlock?.minMaxWidth || {}),
                [dragIndex - 1]: dropWidth || { enabled: false },
                [hoverIndex - 1]: dragWidth || { enabled: false },
            };
            const tempData = tmpBlock?.cells?.map((val: any) => {
                const dragColumn = val[dragIndex - 1];
                const newColumns = [...val];
                newColumns.splice(dragIndex - 1, 1);
                newColumns.splice(hoverIndex - 1, 0, dragColumn);
                return newColumns;
            });
            tmpBlock.cells = tempData;
            setBlock(tmpBlock);
        },
        [table],
    );

    const toolbarProps = {
        curCellRow,
        curCellCol,
        table,
        setBlock,
        setProperty: (v) => {
            const { type, value, is_enabled, subType, path = [] } = v;
            let tmpBlock = _.cloneDeep(table);
            switch (type) {
                case "CORRECT_VALUES":
                    selectedCells.forEach((v) => {
                        if (tmpBlock.cells?.[v[0]]?.[v[1]])
                            tmpBlock.cells[v[0]][v[1]].correctInputValues =
                                value;
                    });
                    break;
                case "HIDDEN":
                    selectedCells.forEach((v) => {
                        if (tmpBlock.cells?.[v[0]]?.[v[1]]) {
                            tmpBlock.cells[v[0]][v[1]].hidden = value;
                        }
                    });
                    break;
                case "ALIGN":
                    selectedCells.forEach((v) => {
                        if (tmpBlock.cells?.[v[0]]?.[v[1]])
                            tmpBlock.cells[v[0]][v[1]].align = value;
                    });
                    break;
                case "ALIGN_VERTICAL":
                    selectedCells.forEach((v) => {
                        if (tmpBlock.cells?.[v[0]]?.[v[1]])
                            tmpBlock.cells[v[0]][v[1]].alignVertical = value;
                    });
                    break;
                case "TEXT":
                    selectedCells.forEach((v) => {
                        if (tmpBlock.cells?.[v[0]]?.[v[1]]) {
                            switch (subType) {
                                case "color":
                                case "size":
                                case "value":
                                    tmpBlock = _.set(
                                        tmpBlock,
                                        [
                                            "cells",
                                            v[0],
                                            v[1],
                                            "text",
                                            subType,
                                            "default",
                                        ],
                                        value,
                                    );
                                    break;
                                case "type":
                                    tmpBlock = _.set(
                                        tmpBlock,
                                        ["cells", v[0], v[1], "text", "type"],
                                        value,
                                    );
                                    tmpBlock = _.set(
                                        tmpBlock,
                                        [
                                            "cells",
                                            v[0],
                                            v[1],
                                            "text",
                                            "value",
                                            "default",
                                        ],
                                        null,
                                    );
                                    break;
                                default:
                                    tmpBlock = _.set(
                                        tmpBlock,
                                        [
                                            "cells",
                                            v[0],
                                            v[1],
                                            "text",
                                            "enabled",
                                        ],
                                        is_enabled,
                                    );
                                    break;
                            }
                        }
                    });
                    break;
                case "FILL_COLOR":
                    selectedCells.forEach((v) => {
                        if (tmpBlock.cells?.[v[0]]?.[v[1]]) {
                            tmpBlock = _.set(
                                tmpBlock,
                                ["cells", v[0], v[1], "fillColor", "default"],
                                is_enabled ? value : "#f5f5f5",
                            );
                            tmpBlock = _.set(
                                tmpBlock,
                                ["cells", v[0], v[1], "fillColor", "enabled"],
                                is_enabled,
                            );
                        }
                    });
                    break;
                case "FILL_IMAGE":
                    selectedCells.forEach((v) => {
                        if (tmpBlock.cells?.[v[0]]?.[v[1]]) {
                            tmpBlock = _.set(
                                tmpBlock,
                                [
                                    "cells",
                                    v[0],
                                    v[1],
                                    "imageBackground",
                                    "src",
                                    "default",
                                ],
                                value,
                            );
                            tmpBlock = _.set(
                                tmpBlock,
                                [
                                    "cells",
                                    v[0],
                                    v[1],
                                    "imageBackground",
                                    "enabled",
                                ],
                                is_enabled,
                            );
                        }
                    });
                    break;
                case "IMAGE":
                    selectedCells.forEach((v) => {
                        if (tmpBlock.cells?.[v[0]]?.[v[1]]) {
                            tmpBlock = _.set(
                                tmpBlock,
                                [
                                    "cells",
                                    v[0],
                                    v[1],
                                    "image",
                                    "src",
                                    "default",
                                ],
                                value,
                            );
                            tmpBlock = _.set(
                                tmpBlock,
                                ["cells", v[0], v[1], "image", "enabled"],
                                is_enabled,
                            );
                        }
                    });
                    break;
                case "PADDING":
                    selectedCells.forEach((v) => {
                        if (tmpBlock.cells?.[v[0]]?.[v[1]]) {
                            switch (subType) {
                                case "horizontal":
                                    tmpBlock = _.set(
                                        tmpBlock,
                                        [
                                            "cells",
                                            v[0],
                                            v[1],
                                            "padding",
                                            "horizontal",
                                        ],
                                        value,
                                    );
                                    break;
                                case "vertical":
                                    tmpBlock = _.set(
                                        tmpBlock,
                                        [
                                            "cells",
                                            v[0],
                                            v[1],
                                            "padding",
                                            "vertical",
                                        ],
                                        value,
                                    );
                                    break;
                                default:
                                    tmpBlock = _.set(
                                        tmpBlock,
                                        ["cells", v[0], v[1], "padding"],
                                        { horizontal: 0, vertical: 0 },
                                    );
                                    break;
                            }
                            tmpBlock = _.set(
                                tmpBlock,
                                ["cells", v[0], v[1], "padding", "enabled"],
                                is_enabled,
                            );
                        }
                    });
                    break;
                case "BORDER":
                    selectedCells.forEach((v) => {
                        if (tmpBlock.cells?.[v[0]]?.[v[1]]) {
                            if (subType) {
                                tmpBlock = _.set(
                                    tmpBlock,
                                    [
                                        "cells",
                                        v[0],
                                        v[1],
                                        "border",
                                        ...path,
                                        subType,
                                    ],
                                    value,
                                );
                                if (subType === "selective") {
                                    if (!tmpBlock?.border?.top) {
                                        tmpBlock = _.set(
                                            tmpBlock,
                                            [
                                                "cells",
                                                v[0],
                                                v[1],
                                                "border",
                                                "top",
                                            ],
                                            {
                                                enabled: false,
                                                color: "#000000",
                                                opacity: 1,
                                                width: 1,
                                                style: "SOLID",
                                            },
                                        );
                                    }
                                    if (!tmpBlock?.border?.bottom) {
                                        tmpBlock = _.set(
                                            tmpBlock,
                                            [
                                                "cells",
                                                v[0],
                                                v[1],
                                                "border",
                                                "bottom",
                                            ],
                                            {
                                                enabled: false,
                                                color: "#000000",
                                                opacity: 1,
                                                width: 1,
                                                style: "SOLID",
                                            },
                                        );
                                    }
                                    if (!tmpBlock?.border?.left) {
                                        tmpBlock = _.set(
                                            tmpBlock,
                                            [
                                                "cells",
                                                v[0],
                                                v[1],
                                                "border",
                                                "left",
                                            ],
                                            {
                                                enabled: false,
                                                color: "#000000",
                                                opacity: 1,
                                                width: 1,
                                                style: "SOLID",
                                            },
                                        );
                                    }
                                    if (!tmpBlock?.border?.right) {
                                        tmpBlock = _.set(
                                            tmpBlock,
                                            [
                                                "cells",
                                                v[0],
                                                v[1],
                                                "border",
                                                "right",
                                            ],
                                            {
                                                enabled: false,
                                                color: "#000000",
                                                opacity: 1,
                                                width: 1,
                                                style: "SOLID",
                                            },
                                        );
                                    }
                                }
                            }
                            tmpBlock = _.set(
                                tmpBlock,
                                ["cells", v[0], v[1], "border", "enabled"],
                                subType ? true : is_enabled,
                            );
                        }
                    });
                    break;
                case "INPUT":
                    selectedCells.forEach((v) => {
                        if (tmpBlock.cells?.[v[0]]?.[v[1]])
                            tmpBlock = _.set(
                                tmpBlock,
                                ["cells", v[0], v[1], "input"],
                                value,
                            );
                    });
                    break;
                case "TAPPABLE":
                    selectedCells.forEach((v) => {
                        if (tmpBlock.cells?.[v[0]]?.[v[1]]) {
                            if (path?.length > 0)
                                tmpBlock = _.set(
                                    tmpBlock,
                                    ["cells", v[0], v[1], "tappable", ...path],
                                    value,
                                );
                            else {
                                tmpBlock = _.set(
                                    tmpBlock,
                                    [
                                        "cells",
                                        v[0],
                                        v[1],
                                        "tappable",
                                        "enabled",
                                    ],
                                    is_enabled,
                                );
                                tmpBlock = _.set(
                                    tmpBlock,
                                    [
                                        "cells",
                                        v[0],
                                        v[1],
                                        "clickable",
                                        "enabled",
                                    ],
                                    is_enabled,
                                );
                            }
                            tmpBlock = _.set(
                                tmpBlock,
                                [
                                    "cells",
                                    v[0],
                                    v[1],
                                    "tappable",
                                    "tap_count",
                                    "default",
                                ],
                                0,
                            );
                        }
                    });
                    break;
                case "DROPDOWN":
                    selectedCells.forEach((v) => {
                        if (tmpBlock.cells?.[v[0]]?.[v[1]])
                            tmpBlock = _.set(
                                tmpBlock,
                                ["cells", v[0], v[1], "dropdown"],
                                value,
                            );
                    });
                    break;
                case "DRAG_DROP":
                    selectedCells.forEach((v) => {
                        if (tmpBlock.cells?.[v[0]]?.[v[1]])
                            tmpBlock = _.set(
                                tmpBlock,
                                ["cells", v[0], v[1], "dragDrop", "enabled"],
                                is_enabled,
                            );
                    });
                    break;
                case "CLICKABLE":
                    selectedCells.forEach((v) => {
                        if (tmpBlock.cells?.[v[0]]?.[v[1]])
                            tmpBlock = _.set(
                                tmpBlock,
                                ["cells", v[0], v[1], "clickable", "enabled"],
                                is_enabled,
                            );
                    });
                    break;
                default:
                    break;
            }
            setBlock(tmpBlock);
        },
        selectedCells: selectedCells
            .map(([row, col]) => table?.cells[row]?.[col])
            .filter(Boolean),
        handleClearSelections: () => setSelectedCells([]),
        handlePasteToCells,
        currentEditor,
        setCurrentEditor,
        disableWrite,
        hasMentions,
        mentionsList,
        allIndicesPresent,
        selectedRows,
        selectedCols,
    };

    return (
        <div style={{ padding: "10px" }}>
            <h4>
                Table Settings:
                <CopyIcon
                    disabled={disableWrite}
                    isButton={true}
                    onClick={() => {
                        message.info(`Copied Table data to clipboard!`);
                        navigator.clipboard.writeText(JSON.stringify(table));
                    }}
                />
            </h4>
            <CellInputPopup {...cellInputProps} />
            {selectedCells?.length > 0 && <TableToolbar {...toolbarProps} />}
            <DndProvider backend={HTML5Backend}>
                <Table
                    bordered
                    columns={columns.map((col, index) => ({
                        ...col,
                        onHeaderCell: (column) => ({
                            index,
                            moveColumn,
                            width: column.width,
                            endCol: index == columns?.length - 1,
                            disableWrite,
                        }),
                    }))}
                    dataSource={tableData}
                    pagination={false}
                    showHeader={true}
                    scroll={{ x: "max-content" }}
                    footer={() =>
                        disableWrite ? null : (
                            <div
                                style={{
                                    display: "flex",
                                    gap: "20px",
                                }}
                            >
                                <Input.Group compact>
                                    <Input
                                        type="number"
                                        onChange={(e) =>
                                            setInputRow(Number(e.target.value))
                                        }
                                        value={inputRow}
                                        style={{
                                            width: "80px",
                                            border: "1px solid block",
                                        }}
                                    />
                                    <Button
                                        disabled={inputRow < 1}
                                        type="primary"
                                        onClick={() => {
                                            let tmpBlock = _.cloneDeep(table);
                                            const numRowsToAdd = inputRow;
                                            const tempData =
                                                !tmpBlock?.cells?.length ||
                                                !tmpBlock?.cells[0]?.length
                                                    ? []
                                                    : tmpBlock?.cells;

                                            for (
                                                let i = 0;
                                                i < numRowsToAdd;
                                                i++
                                            ) {
                                                const newRow = (
                                                    cloneDeep(cells[0]) || [
                                                        ...Array(3),
                                                    ]
                                                ).map(() =>
                                                    cloneDeep(DEFAULT_CELL),
                                                );
                                                tempData.push(newRow);
                                            }
                                            tmpBlock.cells = tempData;
                                            setBlock(tmpBlock);
                                            setInputRow(1);
                                        }}
                                    >
                                        Add Row
                                    </Button>
                                    <Input
                                        type="number"
                                        onChange={(e) =>
                                            setInputCol(Number(e.target.value))
                                        }
                                        value={inputCol}
                                        style={{
                                            width: "80px",
                                        }}
                                    />
                                    <Button
                                        disabled={inputCol < 1}
                                        type="primary"
                                        onClick={() => {
                                            let tmpBlock = _.cloneDeep(table);

                                            const numColsToAdd = inputCol;
                                            tmpBlock?.cells?.forEach(
                                                (row, rowIndex) => {
                                                    for (
                                                        let i = 0;
                                                        i < numColsToAdd;
                                                        i++
                                                    ) {
                                                        const newCell =
                                                            _.cloneDeep(
                                                                DEFAULT_CELL,
                                                            );
                                                        tmpBlock.cells[
                                                            rowIndex
                                                        ].push(newCell);
                                                    }
                                                },
                                            );
                                            setBlock(tmpBlock);
                                            setInputCol(1);
                                        }}
                                    >
                                        Add Column
                                    </Button>
                                </Input.Group>
                                {(!cells?.length || !cells[0]?.length) && (
                                    <span
                                        style={{
                                            color: "red",
                                            fontSize: "16px",
                                        }}
                                    >
                                        No Cells Added
                                    </span>
                                )}
                            </div>
                        )
                    }
                    components={{
                        header: {
                            cell: DraggableHeader,
                        },
                        body: {
                            row: DraggableRow,
                        },
                    }}
                    onRow={(_, index) => ({
                        index,
                        moveRow,
                        disableWrite,
                    })}
                />
            </DndProvider>
            <Collapse>
                <Panel
                    header={
                        <div
                            style={{
                                display: "flex",
                                gap: "20px",
                            }}
                        >
                            <span>Feedback and Evaluation Settings</span>
                            <Button
                                disabled={disableWrite}
                                shape="circle"
                                type={"primary"}
                                size="small"
                                icon={<CopyIcon />}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    message.info(
                                        `Copied Feedback and Evaluation Settings to clipboard!`,
                                    );
                                    navigator.clipboard.writeText(
                                        JSON.stringify({
                                            type: "feedback_eval_table",
                                            data: {
                                                evaluation: table.evaluation,
                                                feedback: table.feedback,
                                            },
                                        }),
                                    );
                                }}
                            />
                        </div>
                    }
                    key="1"
                >
                    <FeedbackAndEvaluation
                        block={table}
                        setBlock={setBlock}
                        disableWrite={disableWrite}
                    />
                </Panel>
                <Panel
                    header={
                        <div
                            style={{
                                display: "flex",
                                gap: "20px",
                            }}
                        >
                            <span>Reorder Settings</span>
                            <Button
                                shape="circle"
                                type={"primary"}
                                size="small"
                                disabled={disableWrite}
                                icon={<CopyIcon />}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    message.info(
                                        `Copied Reorder Settings to clipboard!`,
                                    );
                                    navigator.clipboard.writeText(
                                        JSON.stringify({
                                            type: "reorder_table",
                                            data: {
                                                reorder: table.reorder,
                                            },
                                        }),
                                    );
                                }}
                            />
                        </div>
                    }
                    key="2"
                >
                    <Form.Item label="Reorder Type">
                        <Select
                            disabled={disableWrite}
                            value={table?.reorder?.type}
                            onChange={(val) => {
                                let tmpBlock = _.cloneDeep(table);
                                if (tmpBlock.reorder === undefined)
                                    tmpBlock.reorder = { type: "none" };
                                tmpBlock.reorder.type = val;
                                setBlock(tmpBlock);
                            }}
                            style={{
                                width: "200px",
                            }}
                        >
                            <Select.Option value={null}>None</Select.Option>
                            <Select.Option value="ROW">Row</Select.Option>
                            <Select.Option value="COLUMN">Column</Select.Option>
                        </Select>
                    </Form.Item>
                    {(table.reorder?.type === "ROW" ||
                        table.reorder?.type === "COLUMN") && (
                        <Form.Item
                            label={`Disable reorder for following ${
                                table.reorder.type === "ROW"
                                    ? "rows"
                                    : "columns"
                            }`}
                        >
                            <Select
                                disabled={disableWrite}
                                mode="multiple"
                                value={table?.reorder?.disabled}
                                onChange={(val) => {
                                    let tmpBlock = _.cloneDeep(table);
                                    // delete the disabled key from the cells that are disabled
                                    if (table.reorder.type === "ROW") {
                                        tmpBlock.cells.forEach((row: any) => {
                                            row.forEach((cell: any) => {
                                                cell.blockReorder = false;
                                            });
                                        });
                                    } else if (
                                        table.reorder.type === "COLUMN"
                                    ) {
                                        tmpBlock.cells.forEach((row: any) => {
                                            row.forEach((col: any) => {
                                                col.blockReorder = false;
                                            });
                                        });
                                    }
                                    tmpBlock.reorder.disabled = val;
                                    // Update the respective cells
                                    if (table.reorder.type === "ROW") {
                                        val.forEach((row: number) => {
                                            tmpBlock.cells[row].forEach(
                                                (cell: any) => {
                                                    cell.blockReorder = true;
                                                },
                                            );
                                        });
                                    } else if (
                                        table.reorder.type === "COLUMN"
                                    ) {
                                        tmpBlock.cells.forEach((row: any) => {
                                            val.forEach((col: number) => {
                                                row[col].blockReorder = true;
                                            });
                                        });
                                    }
                                    setBlock(tmpBlock);
                                }}
                            >
                                {table.reorder.type === "ROW"
                                    ? table?.cells?.map((_: any, i: number) => (
                                          <Select.Option value={i}>
                                              {"Row" + " : " + String(i + 1)}
                                          </Select.Option>
                                      ))
                                    : table?.cells[0]?.map(
                                          (_: any, i: number) => (
                                              <Select.Option value={i}>
                                                  {"Column" +
                                                      " : " +
                                                      String(i + 1)}
                                              </Select.Option>
                                          ),
                                      )}
                            </Select>
                        </Form.Item>
                    )}
                </Panel>
                <Panel
                    header={
                        <div
                            style={{
                                display: "flex",
                                gap: "20px",
                            }}
                        >
                            <span>Padding Settings</span>
                            <Button
                                disabled={disableWrite}
                                shape="circle"
                                type={"primary"}
                                size="small"
                                icon={<CopyIcon />}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    message.info(
                                        `Copied Padding Settings to clipboard!`,
                                    );
                                    navigator.clipboard.writeText(
                                        JSON.stringify({
                                            type: "padding_table",
                                            data: {
                                                padding: table.padding,
                                            },
                                        }),
                                    );
                                }}
                            />
                        </div>
                    }
                    key="3"
                >
                    <TablePaddingEditor
                        blockData={table}
                        setBlock={setBlock}
                        disableWrite={disableWrite}
                    />
                </Panel>
                <Panel
                    header={
                        <div
                            style={{
                                display: "flex",
                                gap: "20px",
                            }}
                        >
                            <span>Gap Settings</span>
                            <Button
                                disabled={disableWrite}
                                shape="circle"
                                type={"primary"}
                                size="small"
                                icon={<CopyIcon />}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    message.info(
                                        `Copied Gap Settings to clipboard!`,
                                    );
                                    navigator.clipboard.writeText(
                                        JSON.stringify({
                                            type: "gap_table",
                                            data: {
                                                gap: table.gap,
                                            },
                                        }),
                                    );
                                }}
                            />
                        </div>
                    }
                    key="4"
                >
                    <Row gutter={[12, 12]}>
                        <Col span={12}>
                            <Form.Item label="Enable Custom Gaps">
                                <Switch
                                    disabled={disableWrite}
                                    checkedChildren={<CheckOutlined />}
                                    unCheckedChildren={<CloseOutlined />}
                                    checked={table?.gap?.enabled}
                                    onChange={(value) => {
                                        let tmpCell = _.cloneDeep(table);
                                        tmpCell = _.set(
                                            tmpCell,
                                            ["gap", "enabled"],
                                            value,
                                        );
                                        setBlock(tmpCell);
                                    }}
                                />
                            </Form.Item>
                        </Col>
                        {table?.gap?.enabled && (
                            <>
                                <Col span={12}>
                                    <Form.Item label="Horizontal Gap">
                                        {/*Number field */}
                                        <Input
                                            disabled={disableWrite}
                                            type="number"
                                            value={table?.gap?.horizontal}
                                            onChange={(e) => {
                                                let tmpBlock =
                                                    _.cloneDeep(table);
                                                if (tmpBlock.gap === undefined)
                                                    tmpBlock.gap = {
                                                        horizontal: 0,
                                                    };
                                                tmpBlock.gap.horizontal =
                                                    Number(e.target.value);
                                                setBlock(tmpBlock);
                                            }}
                                            width={200}
                                            min={0}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item label="Vertical Gap">
                                        {/*Number field */}
                                        <Input
                                            disabled={disableWrite}
                                            type="number"
                                            value={table?.gap?.vertical}
                                            onChange={(e) => {
                                                let tmpBlock =
                                                    _.cloneDeep(table);
                                                if (tmpBlock.gap === undefined)
                                                    tmpBlock.gap = {
                                                        vertical: 0,
                                                    };
                                                tmpBlock.gap.vertical = Number(
                                                    e.target.value,
                                                );
                                                setBlock(tmpBlock);
                                            }}
                                            width={200}
                                            min={0}
                                        />
                                    </Form.Item>
                                </Col>
                            </>
                        )}
                    </Row>
                </Panel>
                <Panel
                    header={
                        <div
                            style={{
                                display: "flex",
                                gap: "20px",
                            }}
                        >
                            <span>Border Settings</span>
                            <Button
                                disabled={disableWrite}
                                shape="circle"
                                type={"primary"}
                                size="small"
                                icon={<CopyIcon />}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    message.info(
                                        `Copied Border Settings to clipboard!`,
                                    );
                                    navigator.clipboard.writeText(
                                        JSON.stringify({
                                            type: "border_table",
                                            data: {
                                                border: table.border,
                                            },
                                        }),
                                    );
                                }}
                            />
                        </div>
                    }
                    key="5"
                >
                    <TableBorderEditor
                        blockData={table}
                        setBlock={setBlock}
                        disableWrite={disableWrite}
                    />
                </Panel>
                <Panel
                    header={
                        <div
                            style={{
                                display: "flex",
                                gap: "20px",
                            }}
                        >
                            <span>Interaction Settings</span>
                            <Button
                                shape="circle"
                                type={"primary"}
                                size="small"
                                disabled={disableWrite}
                                icon={<CopyIcon />}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    message.info(
                                        `Copied Interaction Settings to clipboard!`,
                                    );
                                    navigator.clipboard.writeText(
                                        JSON.stringify({
                                            type: "interactionLimit_table",
                                            data: {
                                                interactionLimit:
                                                    table.interactionLimit,
                                            },
                                        }),
                                    );
                                }}
                            />
                        </div>
                    }
                    key="6"
                >
                    <InteractionLimitEditor
                        block={table}
                        setBlock={setBlock}
                        disableWrite={disableWrite}
                    />
                </Panel>
                <Panel
                    header={
                        <div
                            style={{
                                display: "flex",
                                gap: "20px",
                            }}
                        >
                            <span>Alignment, Tap and Swipe Settings</span>
                            <Button
                                shape="circle"
                                type={"primary"}
                                size="small"
                                disabled={disableWrite}
                                icon={<CopyIcon />}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    message.info(
                                        `Copied Alignment, Tap and Swipe Settings to clipboard!`,
                                    );
                                    navigator.clipboard.writeText(
                                        JSON.stringify({
                                            type: "align_table",
                                            data: {
                                                swipable: table.swipable,
                                                align: table.align,
                                                tappable: table.tappable,
                                            },
                                        }),
                                    );
                                }}
                            />
                        </div>
                    }
                    key="x"
                >
                    <AlignmentComponent
                        block={table}
                        setBlock={setBlock}
                        disableWrite={disableWrite}
                    />
                    <Tappable
                        block={table}
                        setBlock={setBlock}
                        disableWrite={disableWrite}
                    />
                    <h3>Swipable</h3>
                    <Form.Item label="Enabled?">
                        <Switch
                            disabled={disableWrite}
                            checkedChildren={<CheckOutlined />}
                            unCheckedChildren={<CloseOutlined />}
                            checked={table?.swipable?.enabled}
                            onChange={(value) => {
                                let tmpCell = _.cloneDeep(table);
                                tmpCell = _.set(
                                    tmpCell,
                                    ["swipable", "enabled"],
                                    value,
                                );
                                setBlock(tmpCell);
                            }}
                        />
                    </Form.Item>
                </Panel>
            </Collapse>
            <Divider />
            {!disableWrite && (
                <Form.Item label={"Paste Table config here"}>
                    <Input.TextArea
                        disabled={disableWrite}
                        value={tempConfig}
                        placeholder="Paste Config here"
                        onChange={(e) => setTempConfig(e.target.value)}
                    />
                    <Button
                        disabled={disableWrite}
                        style={{ marginTop: "10px" }}
                        type="primary"
                        onClick={() => {
                            try {
                                const parsedJson: any = JSON.parse(tempConfig);

                                if (
                                    [
                                        "align_table",
                                        "interactionLimit_table",
                                        "border_table",
                                        "gap_table",
                                        "padding_table",
                                        "reorder_table",
                                        "feedback_eval_table",
                                    ].includes(parsedJson?.type)
                                ) {
                                    setBlock({
                                        ...table,
                                        ...parsedJson?.data,
                                    });
                                } else setBlock(parsedJson);
                                setTempConfig("");
                                message.info("Updated!");
                            } catch (e) {
                                //  captureException(e)
                                message.error("Error in JSON!");
                            }
                        }}
                    >
                        Update table config
                    </Button>
                </Form.Item>
            )}
        </div>
    );
};

const DraggableRow = ({ index, moveRow, disableWrite, ...restProps }) => {
    const ref = useRef();
    const [, drop] = useDrop({
        accept: "row",
        drop(item, monitor) {
            if (!ref.current) {
                return;
            }
            const dragIndex = item.index;
            const hoverIndex = index;
            if (dragIndex === hoverIndex) {
                return;
            }
            // Position checking logic (as in previous example) can be added here for better UX
            moveRow(dragIndex, hoverIndex);
            item.index = hoverIndex;
        },
    });

    const [, drag] = useDrag({
        type: "row",
        item: { index },
    });

    drag(drop(ref));
    return <tr ref={disableWrite ? null : ref} {...restProps} />;
};

const DraggableHeader = ({
    index,
    moveColumn,
    children,
    endCol,
    disableWrite,
    ...restProps
}) => {
    const ref = useRef();
    const [, drop] = useDrop({
        accept: "header",
        drop(item) {
            if (endCol || index === 0 || !ref.current) {
                return;
            }
            const dragIndex = item.index;
            const hoverIndex = index;
            if (dragIndex !== hoverIndex) {
                moveColumn(dragIndex, hoverIndex);
                item.index = hoverIndex;
            }
        },
    });

    const [, drag] = useDrag({
        type: "header",
        item: { index },
    });

    drag(drop(ref));
    return (
        <th
            ref={disableWrite || endCol || index === 0 ? null : ref}
            {...restProps}
        >
            {children}
        </th>
    );
};

export default TableEditor;
